import PayrollPage from './PayrollPage';
import { readBlobError, triggerBlobDownload } from '../utils/exportHelpers';
import DonationPanel from '../components/DonationPanel';
import { printExpenseVoucher } from '../utils/print';
import { AssetsRegisterTab, HotelReportsTab, SpoilageReviewTab } from './AccountantModules';
import { getBrandingSlug } from '../utils/formatters';
import React, { useState, useEffect, useCallback } from 'react';
import { AreaChart } from '../components/DashboardWidgets';
import { PageHeader, StatCards, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES, SparkBars } from '../components/UI';
import * as api from '../api/client';
import axiosClient from '../api/client';

// ─── Approval Stamp SVG ───────────────────────────────────────────────────────
const STAMP_CFG_A = {
  storekeeper: { label:'STOREKEEPER', scaleFont:0.85 },
  manager:     { label:'MANAGER',     scaleFont:1    },
  accountant:  { label:'ACCOUNTANT',  scaleFont:0.82 },
  director:    { label:'DIRECTOR',    scaleFont:0.9  },
  hr:          { label:'HR',          scaleFont:1.1  },
};
function ApprovalStamp({ role, by, at, size=72 }) {
  const cfg = STAMP_CFG_A[role] || { label:role.toUpperCase(), scaleFont:1 };
  const cx = size/2, cy = size/2;
  const rOuter = cx - 3, rInner = cx - 10;
  const arcR = rOuter - 5;
  const uid = `ac_stamp_${role}_${size}`;
  const mainFontSize = Math.round(9 * cfg.scaleFont * (size/72));
  return (
    <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:3 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ opacity:0.9 }}>
        <defs>
          <path id={`${uid}_top`} d={`M ${cx-arcR},${cy} a ${arcR},${arcR} 0 1,1 ${arcR*2},0`}/>
          <filter id={`${uid}_d`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="3" result="n"/>
            <feDisplacementMap in="SourceGraphic" in2="n" scale="0.9" xChannelSelector="R" yChannelSelector="G"/>
          </filter>
        </defs>
        <circle cx={cx} cy={cy} r={rOuter} fill="#fdf6ec" stroke="#8B1A1A" strokeWidth="2.2" filter={`url(#${uid}_d)`}/>
        <circle cx={cx} cy={cy} r={rInner} fill="none" stroke="#8B1A1A" strokeWidth="1.2" filter={`url(#${uid}_d)`}/>
        <text fontSize={Math.max(5,size*0.077)} fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="800" letterSpacing="1.4">
          <textPath href={`#${uid}_top`} startOffset="15%">GRAND MEMORIES</textPath>
        </text>
        <line x1={cx-rInner+5} y1={cy-rInner*0.40} x2={cx+rInner-5} y2={cy-rInner*0.40} stroke="#8B1A1A" strokeWidth="0.9"/>
        <line x1={cx-rInner+5} y1={cy+rInner*0.20} x2={cx+rInner-5} y2={cy+rInner*0.20} stroke="#8B1A1A" strokeWidth="0.9"/>
        <text x={cx} y={cy+1} textAnchor="middle" dominantBaseline="middle" fontSize={mainFontSize}
          fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="900" letterSpacing="0.3"
          filter={`url(#${uid}_d)`}>{cfg.label}</text>
        <text x={cx} y={cy+rInner*0.38} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4.5,size*0.069)} fill="#8B1A1A"
          fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="600" letterSpacing="1.8">— APPROVED —</text>
        <text x={cx-rInner*0.55} y={cy+rInner*0.66} textAnchor="middle" fontSize={Math.max(5,size*0.09)} fill="#8B1A1A">★</text>
        <text x={cx+rInner*0.55} y={cy+rInner*0.66} textAnchor="middle" fontSize={Math.max(5,size*0.09)} fill="#8B1A1A">★</text>
        <text x={cx} y={cy+rInner*0.85} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4,size*0.069)} fill="#8B1A1A"
          fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="700" letterSpacing="1.6">RESORT &amp; SPA</text>
      </svg>
      {(by||at) && (
        <div style={{ fontSize:9, color:'#888', textAlign:'center', maxWidth:size, lineHeight:1.3 }}>
          {by && <div style={{ fontWeight:600 }}>{by}</div>}
          {at  && <div>{new Date(at).toLocaleDateString('en-KE',{day:'2-digit',month:'short',year:'numeric'})}</div>}
        </div>
      )}
    </div>
  );
}

const PERIODS = [
  { id:'day',label:'Today' },{ id:'week',label:'This Week' },
  { id:'month',label:'This Month' },{ id:'year',label:'This Year' },
  { id:'custom',label:'Custom Range' },
];

const EXP_CATS = [
  { id:'payroll',label:'Payroll & Salaries' },{ id:'utilities',label:'Utilities' },
  { id:'supplies',label:'Supplies' },{ id:'maintenance',label:'Maintenance & Repairs' },
  { id:'marketing',label:'Marketing' },{ id:'rent',label:'Rent & Lease' },
  { id:'food_bev',label:'Food & Beverage Purchases' },{ id:'laundry',label:'Laundry & Linen' },
  { id:'transport',label:'Transport & Fuel' },{ id:'tax',label:'Taxes & Levies' },
  { id:'insurance',label:'Insurance' },{ id:'other',label:'Other' },
];

const EXP_ICONS = {
  payroll:'👥',utilities:'⚡',supplies:'📦',maintenance:'🔧',marketing:'📣',
  rent:'🏢',food_bev:'🛒',laundry:'👔',transport:'🚗',tax:'🧾',insurance:'🛡️',other:'📌',
};

const PAY_MODES = ['cash','mpesa','bank','pdq'];

export default function AccountantPage({ notify }) {
  const [tab,setTab]           = useState('dashboard');
  const [period,setPeriod]     = useState('month');
  const [dateFrom,setDateFrom] = useState('');
  const [dateTo,setDateTo]     = useState('');
  const [data,setData]         = useState(null);
  const [loading,setLoading]   = useState(false);

  // Expenses state
  const [expenses,setExpenses]         = useState([]);
  const [expFilter,setExpFilter]       = useState({ category:'',date_from:'',date_to:'',search:'',status:'' });
  const [expModal,setExpModal]         = useState(false);
  const [editExpense,setEditExpense]   = useState(null);
  const [expForm,setExpForm]           = useState({ category:'other',description:'',amount:'',vendor:'',invoice_number:'',expense_date:new Date().toISOString().slice(0,10),payment_mode:'cash',receipt_ref:'',notes:'' });
  const [savingExp,setSavingExp]       = useState(false);


  // Shopping Lists (accountant approval)
  const [shoppingLists,setShoppingLists]   = useState([]);
  const [fundsDispatched,setFundsDispatched] = useState([]);
  const [fdModal,setFdModal]               = useState(false);
  const [fdForm,setFdForm]                 = useState({ shopping_list:'',amount:'',method:'cash',dispatched_to:'',reference:'',notes:'' });
  const [fdSaving,setFdSaving]             = useState(false);
  const [selectedSL,setSelectedSL]         = useState(null);

  // Build query params from period
  const buildParams = useCallback(() => {
    let p = `?period=${period}`;
    if (period==='custom'&&dateFrom&&dateTo) p+=`&date_from=${dateFrom}&date_to=${dateTo}`;
    return p;
  },[period,dateFrom,dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getAccountantReports(buildParams());
      setData(r.data);
    } catch(e){ notify('Error loading reports','error'); }
    finally{ setLoading(false); }
  },[buildParams]);

  const loadExpenses = useCallback(async () => {
    let p = '?';
    if(expFilter.category) p+=`category=${expFilter.category}&`;
    if(expFilter.date_from) p+=`date_from=${expFilter.date_from}&`;
    if(expFilter.date_to) p+=`date_to=${expFilter.date_to}&`;
    if(expFilter.search) p+=`search=${encodeURIComponent(expFilter.search)}&`;
    if(expFilter.status) p+=`status=${expFilter.status}&`;
    const r = await api.getExpenses(p);
    setExpenses(r.data.results||r.data);
  },[expFilter]);

  useEffect(()=>{ load(); },[load]);
  useEffect(()=>{ if(tab==='expenses') loadExpenses(); },[tab,loadExpenses]);
  useEffect(()=>{ 
    if(tab==='shopping_approval') {
      api.getShoppingLists().then(r => setShoppingLists(r.data.results||r.data)).catch(()=>{});
    }
    if(tab==='funds_dispatched') {
      api.getFundsDispatched().then(r => setFundsDispatched(r.data.results||r.data)).catch(()=>{});
      api.getShoppingLists().then(r => setShoppingLists(r.data.results||r.data)).catch(()=>{});
    }
  },[tab]);

  const doRollover = async () => {
    try{ const r=await api.performRollover(); notify(r.data.message||'Rollover complete!'); await load(); }
    catch(e){ notify(e.response?.data?.error||'Rollover error','error'); }
  };

  const doExportExcel = async () => {
    const p = buildParams();
    notify('Preparing Excel export…');
    try {
      const res = await axiosClient.get(api.exportExcel(p), { responseType: 'blob', baseURL: '', timeout: 120000 });
      // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res.data;
      if (blob.size < 50) throw new Error('Empty file — check server logs');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${getBrandingSlug()}_Report_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      notify('Excel report downloaded ✓');
    } catch(e) {
      const msg = e.name === 'TimeoutError' ? 'Export timed out — try a shorter date range' : await readBlobError(e);
      notify(`Excel export failed: ${msg}`, 'error');
    }
  };

  const doExportPdf = async () => {
    const p = buildParams();
    notify('Preparing PDF export…');
    try {
      const res = await axiosClient.get(api.exportPdf(p), { responseType: 'blob', baseURL: '', timeout: 120000 });
      // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res.data;
      if (blob.size < 50) throw new Error('Empty file — check server logs');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${getBrandingSlug()}_Report_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      notify('PDF report downloaded ✓');
    } catch(e) {
      const msg = e.name === 'TimeoutError' ? 'Export timed out — try a shorter date range' : await readBlobError(e);
      notify(`PDF export failed: ${msg}`, 'error');
    }
  };

  const openExpModal = (exp=null) => {
    if(exp){ setEditExpense(exp); setExpForm({ category:exp.category,description:exp.description,amount:exp.amount,vendor:exp.vendor||'',invoice_number:exp.invoice_number||'',expense_date:exp.expense_date,payment_mode:exp.payment_mode||'cash',receipt_ref:exp.receipt_ref||'',notes:exp.notes||'' }); }
    else{ setEditExpense(null); setExpForm({ category:'other',description:'',amount:'',vendor:'',invoice_number:'',expense_date:new Date().toISOString().slice(0,10),payment_mode:'cash',receipt_ref:'',notes:'' }); }
    setExpModal(true);
  };

  const saveExpense = async () => {
    if(!expForm.description.trim()||!expForm.amount||!expForm.expense_date){ notify('Fill required fields','error'); return; }
    if(parseFloat(expForm.amount)<=0){ notify('Amount must be positive','error'); return; }
    setSavingExp(true);
    try{
      if(editExpense) await api.updateExpense(editExpense.id,expForm);
      else await api.createExpense(expForm);
      setExpModal(false);
      await loadExpenses();
      await load();
      notify(editExpense?'Expense updated!':'Expense recorded!');
    }catch(e){ notify(e.response?.data?.description?.[0]||'Error','error'); }
    finally{ setSavingExp(false); }
  };

  const deleteExp = async (id) => {
    if(!window.confirm('Delete this expense?')) return;
    await api.deleteExpense(id);
    await loadExpenses();
    await load();
    notify('Expense deleted');
  };

  const expTotal = expenses.reduce((s,e)=>s+parseFloat(e.amount||0),0);

  if(!data&&loading) return <div style={{textAlign:'center',padding:'60px',color:'var(--textFaint)'}}>Loading…</div>;

  const rev     = data?.revenue||{};
  const exp_d   = data?.expenses||{};
  const rooms_s = data?.rooms||{};
  const guests  = data?.guests||{};
  const pmethods= data?.payment_methods||{};
  const trend   = (data?.daily_trend||[]).slice(-14);

  const periodParams = buildParams();

  return (
    <div>
      <PageHeader icon="📊" title="Accounting & Reports" sub="Financial statements, revenue analysis, expenses & compliance"
        action={
          <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
            <button style={{...btnSm,background:'var(--bgSuccess)',color:'#2ecc71',padding:'7px 12px'}} onClick={doRollover}>🔄 Rollover</button>
            <button className="btn btn-default btn-sm" onClick={doExportExcel}>📊 Excel</button>
            <button className="btn btn-danger btn-sm" onClick={doExportPdf}>📄 PDF</button>
          </div>
        }
      />

      {/* Period selector */}
      <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap',alignItems:'center',padding:'12px 14px',background:'var(--bgCard)',border:'1px solid #1e1e1e',borderRadius:4}}>
        <span style={{fontSize:11,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',marginRight:4}}>Period:</span>
        {PERIODS.map(p=>(
          <button key={p.id} onClick={()=>setPeriod(p.id)} style={{
            background:period===p.id?'#c8a96e':'transparent',border:`1px solid ${period===p.id?'#c8a96e':'var(--borderMid)'}`,
            color:period===p.id?'#111':'#888',borderRadius:3,padding:'5px 12px',fontSize:12,cursor:'pointer',fontFamily:'inherit',
          }}>{p.label}</button>
        ))}
        {period==='custom'&&(
          <>
            <input type="date" style={{...inputStyle,width:140,padding:'5px 8px'}} value={dateFrom} onChange={e=>setDateFrom(e.target.value)}/>
            <span style={{color:'var(--textFaint)',fontSize:12}}>→</span>
            <input type="date" style={{...inputStyle,width:140,padding:'5px 8px'}} value={dateTo} onChange={e=>setDateTo(e.target.value)}/>
            <button style={{...btnSm,background:'var(--accent)',color:'#111',padding:'5px 14px'}} onClick={load}>Apply</button>
          </>
        )}
        {data&&<div style={{fontSize:11,color:'#444',marginLeft:'auto'}}>{data.period?.start} → {data.period?.end} · {data.transactions_count} tx</div>}
        {/* Inline exports for current filter */}
        <button className="btn btn-default btn-sm" onClick={doExportExcel}>⬇ XLSX</button>
        <button className="btn btn-danger btn-sm" onClick={doExportPdf}>⬇ PDF</button>
      </div>

      <TabBar tabs={[
        {id:'dashboard',label:'Dashboard',icon:'📊'},
        {id:'income',label:'Income Statement',icon:'💰'},
        {id:'expenses',label:'Expenses',icon:'💸'},
        {id:'rooms',label:'Occupancy',icon:'🏨'},
        {id:'payments',label:'Payments',icon:'💳'},
        {id:'vat',label:'VAT Report',icon:'🧾'},
        {id:'rollover',label:'Daily Log',icon:'🔄'},
        {id:'payroll',label:'Payroll',icon:'👥'},
        {id:'shopping_approval',label:`Purchase Requests${shoppingLists.filter(s=>s.status==='manager_approved').length>0?` (${shoppingLists.filter(s=>s.status==='manager_approved').length})`:''}`,icon:'🛒'},
        {id:'funds_dispatched',label:'Funds Dispatched',icon:'💵'},
        {id:'audit_export',label:'Audit & Logs',icon:'📋'},
        {id:'donations',label:'Donations',icon:'🌿'},
        {id:'assets',label:'Assets Register',icon:'🏗️'},
        {id:'hotel_reports',label:'Hotel Reports',icon:'📑'},
        {id:'spoilage_review',label:'Spoilage Review',icon:'🗑️'},
      ]} active={tab} onChange={setTab}/>

      {/* ── DASHBOARD ── */}
      {tab==='dashboard'&&data&&(
        <>
          <StatCards stats={[
            {label:'Total Revenue', value:fmtKES(rev.total),         color:'var(--accent)', icon:'💰', spark:trend.slice(-7).map(d=>d.revenue)},
            {label:'Total Expenses',value:fmtKES(exp_d.total),        color:'#e74c3c', icon:'📉', spark:trend.slice(-7).map(d=>d.expense)},
            {label:'Net Profit',    value:fmtKES(exp_d.net_profit),   color:exp_d.net_profit>=0?'#2ecc71':'#e74c3c', icon:'📊', sub: exp_d.net_profit>=0 ? 'Profitable' : 'Loss period'},
            {label:'Occupancy',     value:`${rooms_s.occupancy_rate}%`,color:'#3498db', icon:'🏨'},
          ]}/>

          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:12}}>
            <Card title="Revenue by Department">
              {[
                {label:'🛏️ Rooms',value:rev.room,color:'#3498db'},
                {label:'🍽️ Food',value:rev.food,color:'#e67e22'},
                {label:'🍹 Bar',value:rev.bar,color:'#9b59b6'},
                {label:'🏊 Pool',value:rev.pool,color:'#1abc9c'},
                {label:'🏛️ Conference',value:rev.conference,color:'var(--accent)'},
              ].map(d=>(
                <div key={d.label} style={{marginBottom:10}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:3,fontSize:13}}>
                    <span style={{color:'var(--textMuted)'}}>{d.label}</span>
                    <span style={{fontWeight:700,color:d.color}}>{fmtKES(d.value)}</span>
                  </div>
                  <div style={{height:5,background:'var(--border)',borderRadius:3}}>
                    <div style={{height:'100%',width:`${rev.total?(Math.min(100,(d.value/rev.total)*100)):0}%`,background:d.color,borderRadius:3,transition:'width .5s'}}/>
                  </div>
                  <div style={{fontSize:10,color:'#444',marginTop:2}}>{rev.total?`${((d.value/rev.total)*100).toFixed(1)}%`:'—'}</div>
                </div>
              ))}
            </Card>

            <Card title={`Revenue vs Expenses — Last ${trend.length} Days`}>
              {trend.length>0?(
                <>
                  <AreaChart data={trend.map(d=>({label:d.date?.slice(5),value:d.revenue}))} color="#c8a96e" height={80} formatY={v=>'K'+Math.round(v/1000)}/>
                  <div style={{height:6}}/>
                  <AreaChart data={trend.map(d=>({label:d.date?.slice(5),value:d.expense}))} color="#e74c3c" height={60} formatY={v=>'K'+Math.round(v/1000)}/>
                  {trend.slice(-4).reverse().map(d=>(
                    <div key={d.date} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:'1px solid #1a1a1a',fontSize:12}}>
                      <span style={{color:'var(--textFaint)'}}>{d.date}</span>
                      <span style={{color:'var(--accent)'}}>{fmtKES(d.revenue)}</span>
                      <span style={{color:'#e74c3c'}}>({fmtKES(d.expense)})</span>
                      <span style={{color:d.revenue-d.expense>=0?'#2ecc71':'#e74c3c',fontWeight:700}}>{fmtKES(d.revenue-d.expense)}</span>
                    </div>
                  ))}
                </>
              ):<Empty text="No data for period"/>}
            </Card>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:8}}>
            {[
              {label:'ADR',value:`KES ${rooms_s.adr?.toLocaleString('en-KE')||'—'}`,sub:'Average Daily Rate',color:'#3498db'},
              {label:'RevPAR',value:`KES ${rooms_s.revpar?.toLocaleString('en-KE')||'—'}`,sub:'Rev Per Available Room',color:'#9b59b6'},
              {label:'Net (ex-VAT)',value:fmtKES(rev.net_ex_vat),sub:'Revenue after VAT',color:'#2ecc71'},
              {label:'VAT Collected',value:fmtKES(rev.vat_collected),sub:'Output VAT @16%',color:'#e67e22'},
              {label:'Outstanding',value:fmtKES(data.outstanding_folios),sub:'Unsettled folios',color:'#e74c3c'},
              {label:'Active Guests',value:guests.active,sub:'Currently staying',color:'#1abc9c'},
            ].map(m=>(
              <div key={m.label} style={{background:'var(--bgCard)',border:'1px solid #1e1e1e',borderLeft:`3px solid ${m.color}`,borderRadius:4,padding:'11px 12px'}}>
                <div style={{fontSize:10,color:'#444',letterSpacing:1,textTransform:'uppercase',marginBottom:3}}>{m.label}</div>
                <div style={{fontSize:18,fontWeight:700,color:m.color}}>{m.value}</div>
                <div style={{fontSize:10,color:'#333',marginTop:2}}>{m.sub}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── INCOME STATEMENT ── */}
      {tab==='income'&&data&&(
        <Card title={`Income Statement — ${data.period?.start} to ${data.period?.end}`}
          action={<div style={{display:'flex',gap:6}}><button className="btn btn-default btn-sm" onClick={doExportExcel}>📊 Excel</button><button className="btn btn-danger btn-sm" onClick={doExportPdf}>📄 PDF</button></div>}>
          <div style={{maxWidth:560}}>
            {[
              {head:'REVENUE',items:[
                ['Room Revenue',rev.room],['Restaurant Revenue',rev.food],['Bar Revenue',rev.bar],
                ['Pool Revenue',rev.pool],['Conference Revenue',rev.conference],
                ['Gift Shop Revenue',rev.gift_shop||0],['Kids Amenities',rev.kids_amenities||0],
                ['Transport Revenue',rev.transport||0],['Donations',rev.donations],
              ],total:rev.total,tColor:'#c8a96e'},
              {head:'EXPENSES',items:(exp_d.by_category||[]).map(e=>[e.label,e.amount]),total:exp_d.total,tColor:'#e74c3c'},
            ].map(section=>(
              <div key={section.head} style={{marginBottom:16}}>
                <div style={{fontSize:11,color:'var(--textFaint)',letterSpacing:2,textTransform:'uppercase',marginBottom:6,paddingBottom:6,borderBottom:`2px solid ${section.tColor}33`}}>{section.head}</div>
                {section.items.map(([l,v])=>(
                  <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid #1a1a1a',fontSize:13}}>
                    <span style={{color:'var(--textMuted)',paddingLeft:8}}>{l}</span>
                    <span style={{color:'var(--textSecond)',fontWeight:500}}>{fmtKES(v)}</span>
                  </div>
                ))}
                <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:`2px solid ${section.tColor}44`,fontSize:14,fontWeight:700}}>
                  <span style={{color:'var(--textPrimary)'}}>Total {section.head.charAt(0)+section.head.slice(1).toLowerCase()}</span>
                  <span style={{color:section.tColor}}>{fmtKES(section.total)}</span>
                </div>
              </div>
            ))}
            <div style={{display:'flex',justifyContent:'space-between',padding:'10px 0',borderBottom:'1px solid #1a1a1a',fontSize:13}}>
              <span style={{color:'var(--textMuted)'}}>Gross Revenue (VAT Inclusive)</span>
              <span style={{color:'var(--textPrimary)',fontWeight:600}}>{fmtKES(rev.total)}</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid #1a1a1a',fontSize:13}}>
              <span style={{color:'var(--textMuted)'}}>VAT (16%)</span>
              <span style={{color:'#e74c3c'}}>({fmtKES(rev.vat_collected)})</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid #1a1a1a',fontSize:13}}>
              <span style={{color:'var(--textMuted)'}}>Catering Levy (2% on Rooms)</span>
              <span style={{color:'#e74c3c'}}>({fmtKES(rev.catering_levy||0)})</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid #1a1a1a',fontSize:13}}>
              <span style={{color:'var(--textMuted)'}}>Net Revenue (ex-VAT)</span>
              <span style={{color:'#2ecc71',fontWeight:700}}>{fmtKES(rev.net_ex_vat)}</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid #1a1a1a',fontSize:13}}>
              <span style={{color:'var(--textMuted)'}}>Total Expenses</span>
              <span style={{color:'#e74c3c'}}>({fmtKES(exp_d.total)})</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'12px 14px',background:exp_d.net_profit>=0?'#0a1a0a':'#1a0a0a',border:`1px solid ${exp_d.net_profit>=0?'#2ecc7133':'#e74c3c33'}`,borderRadius:4,marginTop:10,fontSize:16,fontWeight:700}}>
              <span style={{color:'var(--textPrimary)'}}>NET PROFIT / (LOSS)</span>
              <span style={{color:exp_d.net_profit>=0?'#2ecc71':'#e74c3c'}}>{fmtKES(exp_d.net_profit)}</span>
            </div>
          </div>
        </Card>
      )}

      {/* ── EXPENSES ── */}
      {tab==='expenses'&&(
        <>
          <Card title="Expense Filters"
            action={<button style={{...btnSm,background:'var(--accent)',color:'#111',padding:'6px 14px',fontWeight:700}} onClick={()=>openExpModal()}>+ Record Expense</button>}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:10}}>
              <Field label="Category">
                <select style={inputStyle} value={expFilter.category} onChange={e=>setExpFilter(f=>({...f,category:e.target.value}))}>
                  <option value="">All Categories</option>
                  {EXP_CATS.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="From Date"><input type="date" style={inputStyle} value={expFilter.date_from} onChange={e=>setExpFilter(f=>({...f,date_from:e.target.value}))}/></Field>
              <Field label="To Date"><input type="date" style={inputStyle} value={expFilter.date_to} onChange={e=>setExpFilter(f=>({...f,date_to:e.target.value}))}/></Field>
              <Field label="Status">
                <select style={inputStyle} value={expFilter.status} onChange={e=>setExpFilter(f=>({...f,status:e.target.value}))}>
                  <option value="">All</option><option value="approved">Approved</option><option value="pending">Pending</option>
                </select>
              </Field>
              <Field label="Search">
                <input style={inputStyle} placeholder="Vendor, description…" value={expFilter.search} onChange={e=>setExpFilter(f=>({...f,search:e.target.value}))}/>
              </Field>
            </div>
            <div style={{display:'flex',gap:8,marginTop:10,flexWrap:'wrap',alignItems:'center'}}>
              <button style={{...btnSm,background:'#c8a96e22',color:'var(--accent)',border:'1px solid #c8a96e44',padding:'7px 16px'}} onClick={loadExpenses}>🔍 Apply Filter</button>
              <button style={{...btnSm,padding:'7px 12px'}} onClick={()=>{setExpFilter({category:'',date_from:'',date_to:'',search:'',status:''});}}>✕ Clear</button>
              <div style={{fontSize:13,color:'var(--accent)',fontWeight:700,marginLeft:'auto'}}>{expenses.length} records · Total: {fmtKES(expTotal)}</div>
            </div>
          </Card>

          {/* Expense summary by category */}
          {expenses.length>0&&(
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:8,marginBottom:12}}>
              {Object.entries(expenses.reduce((acc,e)=>{const k=e.category;acc[k]=(acc[k]||0)+parseFloat(e.amount);return acc;},{})).map(([cat,amt])=>(
                <div key={cat} style={{background:'var(--bgCard)',border:'1px solid #1e1e1e',borderRadius:4,padding:'10px 12px',display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:20}}>{EXP_ICONS[cat]||'📌'}</span>
                  <div>
                    <div style={{fontSize:11,color:'var(--textMuted)',textTransform:'uppercase',letterSpacing:1}}>{EXP_CATS.find(c=>c.id===cat)?.label||cat}</div>
                    <div style={{fontSize:16,fontWeight:700,color:'#e74c3c'}}>{fmtKES(amt)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Expense list */}
          <Card>
            {expenses.length===0?<Empty text="No expenses match filter"/>:(
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr>{['Date','Category','Description','Vendor','Invoice #','Amount','Mode','Status',''].map(h=>(
                      <th key={h} style={{background:'#0e0e0e',padding:'7px 9px',textAlign:'left',fontSize:9,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',whiteSpace:'nowrap'}}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {expenses.map(e=>(
                      <tr key={e.id} style={{borderBottom:'1px solid #1a1a1a'}}>
                        <td style={{padding:'8px 9px',color:'var(--textMuted)',whiteSpace:'nowrap'}}>{e.expense_date}</td>
                        <td style={{padding:'8px 9px',color:'var(--textMuted)'}}><span style={{fontSize:12}}>{EXP_ICONS[e.category]||'📌'}</span> {e.category_label}</td>
                        <td style={{padding:'8px 9px',color:'var(--textMuted)',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.description}</td>
                        <td style={{padding:'8px 9px',color:'var(--textMuted)'}}>{e.vendor||'—'}</td>
                        <td style={{padding:'8px 9px',color:'var(--textFaint)',fontFamily:'monospace',fontSize:11}}>{e.invoice_number||'—'}</td>
                        <td style={{padding:'8px 9px',fontWeight:700,color:'#e74c3c',whiteSpace:'nowrap'}}>{fmtKES(e.amount)}</td>
                        <td style={{padding:'8px 9px',color:'var(--textMuted)',textTransform:'uppercase',fontSize:11}}>{e.payment_mode}</td>
                        <td style={{padding:'8px 9px'}}>
                          <span style={{padding:'2px 7px',borderRadius:3,fontSize:10,fontWeight:700,background:e.status==='approved'?'var(--bgSuccess)':'var(--bgWarning)',color:e.status==='approved'?'#16a34a':'#d97706'}}>{e.status}</span>
                        </td>
                        <td style={{padding:'8px 9px',whiteSpace:'nowrap'}}>
                          <button style={{...btnSm,fontSize:11,marginRight:4}} onClick={()=>printExpenseVoucher(e)}>🖨</button>
                          <button style={{...btnSm,fontSize:11,marginRight:4}} onClick={()=>openExpModal(e)}>Edit</button>
                          <button style={{...btnSm,fontSize:11,color:'#e74c3c',background:'#1a0a0a'}} onClick={()=>deleteExp(e.id)}>Del</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ── OCCUPANCY ── */}
      {tab==='rooms'&&data&&(
        <div style={{display:'grid',gap:12}}>
          <StatCards stats={[
            {label:'Occupancy Rate',value:`${rooms_s.occupancy_rate}%`,color:'#2ecc71'},
            {label:'ADR',value:`KES ${rooms_s.adr?.toLocaleString('en-KE')||0}`,color:'#3498db'},
            {label:'RevPAR',value:`KES ${rooms_s.revpar?.toLocaleString('en-KE')||0}`,color:'#9b59b6'},
            {label:'Room Revenue',value:fmtKES(rev.room),color:'var(--accent)'},
          ]}/>
          <Card title="Room & Guest Detail">
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:10}}>
              {[
                {label:'Total Rooms',value:rooms_s.total,color:'var(--textMuted)'},
                {label:'Occupied',value:rooms_s.occupied,color:'#e67e22'},
                {label:'Available',value:rooms_s.available,color:'#2ecc71'},
                {label:'Guests Checked In',value:guests.checked_in,color:'#3498db'},
                {label:'Guests Checked Out',value:guests.checked_out,color:'#9b59b6'},
                {label:'Currently Staying',value:guests.active,color:'var(--accent)'},
              ].map(m=>(
                <div key={m.label} style={{background:'var(--bgCard)',border:'1px solid #1e1e1e',borderRadius:4,padding:'12px',textAlign:'center'}}>
                  <div style={{fontSize:28,fontWeight:700,color:m.color}}>{m.value}</div>
                  <div style={{fontSize:10,color:'var(--textFaint)',textTransform:'uppercase',letterSpacing:1,marginTop:4}}>{m.label}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* ── PAYMENT METHODS ── */}
      {tab==='payments'&&data&&(
        <Card title="Payment Method Breakdown" action={<div style={{display:'flex',gap:6}}><button className="btn btn-default btn-sm" onClick={doExportExcel}>📊 Excel</button><button className="btn btn-danger btn-sm" onClick={doExportPdf}>📄 PDF</button></div>}>
          {[
            {label:'💵 Cash',value:pmethods.cash,color:'#2ecc71'},
            {label:'📱 M-Pesa',value:pmethods.mpesa,color:'#00b300'},
            {label:'🏦 Bank Transfer',value:pmethods.bank,color:'#3498db'},
            {label:'💳 PDQ / Card',value:pmethods.pdq,color:'#9b59b6'},
            {label:'🏨 Room Charge',value:pmethods.room_charge,color:'#e67e22'},
          ].map(m=>{
            const pct=rev.total?((m.value||0)/rev.total*100).toFixed(1):0;
            return(
              <div key={m.label} style={{marginBottom:14}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:5,fontSize:14}}>
                  <span style={{color:'var(--textMuted)'}}>{m.label}</span>
                  <div style={{textAlign:'right'}}>
                    <span style={{fontWeight:700,color:m.color}}>{fmtKES(m.value||0)}</span>
                    <span style={{fontSize:11,color:'var(--textFaint)',marginLeft:8}}>{pct}%</span>
                  </div>
                </div>
                <div style={{height:8,background:'var(--border)',borderRadius:4}}>
                  <div style={{height:'100%',width:`${pct}%`,background:m.color,borderRadius:4,transition:'width .5s'}}/>
                </div>
              </div>
            );
          })}
          <div style={{paddingTop:12,borderTop:'1px solid #1e1e1e',display:'flex',justifyContent:'space-between',fontSize:15,fontWeight:700}}>
            <span style={{color:'var(--textPrimary)'}}>Total</span>
            <span style={{color:'var(--accent)'}}>{fmtKES(rev.total)}</span>
          </div>
        </Card>
      )}

      {/* ── VAT REPORT ── */}
      {tab==='vat'&&data&&(
        <Card title={`VAT Report — ${data.period?.start} to ${data.period?.end}`} action={<button className="btn btn-danger btn-sm" onClick={doExportPdf}>📄 PDF</button>}>
          <div style={{maxWidth:500}}>
            <div style={{padding:'12px 14px',background:'var(--bgInfo)',border:'1px solid #c8a96e33',borderRadius:4,marginBottom:14}}>
              <div style={{fontSize:11,color:'var(--accent)',letterSpacing:2,textTransform:'uppercase',marginBottom:4}}>Kenya Revenue Authority · Standard VAT 16%</div>
              <div style={{fontSize:12,color:'var(--textMuted)'}}>Output VAT on all taxable hotel supplies at 16% standard rate</div>
            </div>
            {[
              ['Gross Revenue (VAT Inclusive)',fmtKES(rev.total),'#eee'],
              ['Net Revenue (VAT Exclusive)',fmtKES(rev.net_ex_vat),'#2ecc71'],
              ['Output VAT Collected (16/116 × Gross)',fmtKES(rev.vat_collected),'#c8a96e'],
              ['Catering Levy Collected (2% of Room Revenue)',fmtKES(rev.catering_levy||0),'#e67e22'],
            ].map(([l,v,c])=>(
              <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'9px 0',borderBottom:'1px solid #1e1e1e',fontSize:14}}>
                <span style={{color:'var(--textMuted)'}}>{l}</span>
                <span style={{fontWeight:700,color:c}}>{v}</span>
              </div>
            ))}
            <div style={{marginTop:14,padding:'14px',background:'#0a1a1a',border:'1px solid #3498db33',borderRadius:4}}>
              <div style={{fontSize:11,color:'#3498db',letterSpacing:2,textTransform:'uppercase',marginBottom:4}}>Net Output VAT Payable</div>
              <div style={{fontSize:22,fontWeight:700,color:'#3498db'}}>{fmtKES(rev.vat_collected)}</div>
              <div style={{fontSize:11,color:'var(--textFaint)',marginTop:4}}>File monthly by 20th of the following month · KRA iTax portal</div>
            </div>
            <div style={{marginTop:10,padding:'14px',background:'#1a0f00',border:'1px solid #e67e2233',borderRadius:4}}>
              <div style={{fontSize:11,color:'#e67e22',letterSpacing:2,textTransform:'uppercase',marginBottom:4}}>Catering Levy Payable (2%)</div>
              <div style={{fontSize:22,fontWeight:700,color:'#e67e22'}}>{fmtKES(rev.catering_levy||0)}</div>
              <div style={{fontSize:11,color:'var(--textFaint)',marginTop:4}}>Statutory 2% catering levy on hotel accommodation (room charges) — remit to Tourism Fund</div>
            </div>
            <div style={{marginTop:10,padding:'10px 12px',background:'var(--bgCard)',borderRadius:4,fontSize:11,color:'var(--textFaint)'}}>
              Note: Input VAT on purchases tracked in Expenses module. Net VAT payable = Output VAT − Input VAT. Catering levy applies to room revenue only. Consult KRA or tax advisor for final filing.
            </div>
          </div>
        </Card>
      )}

      {/* ── ROLLOVER ── */}
      {tab==='rollover'&&(
        <Card title="Daily Rollover Log" action={<button style={{...btnSm,background:'var(--bgSuccess)',color:'#2ecc71',padding:'7px 14px'}} onClick={doRollover}>🔄 Run Now</button>}>
          {!data?.rollovers?.length?<Empty text="No rollover records for this period"/>:(
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:600}}>
                <thead>
                  <tr>{['Date','Total Rev','Rooms','F&B','Pool','Conf','Guests In','Guests Out','Active'].map(h=>(
                    <th key={h} style={{background:'#0e0e0e',padding:'7px 8px',textAlign:'left',fontSize:9,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',whiteSpace:'nowrap'}}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {data.rollovers.map(r=>(
                    <tr key={r.id} style={{borderBottom:'1px solid #1a1a1a'}}>
                      <td style={{padding:'7px 8px',color:'var(--accent)',fontWeight:700}}>{r.date}</td>
                      <td style={{padding:'7px 8px',color:'var(--textPrimary)',fontWeight:700}}>{fmtKES(r.total_revenue)}</td>
                      <td style={{padding:'7px 8px',color:'#3498db'}}>{fmtKES(r.room_revenue)}</td>
                      <td style={{padding:'7px 8px',color:'#e67e22'}}>{fmtKES(parseFloat(r.food_revenue)+parseFloat(r.bar_revenue))}</td>
                      <td style={{padding:'7px 8px',color:'#1abc9c'}}>{fmtKES(r.pool_revenue)}</td>
                      <td style={{padding:'7px 8px',color:'#9b59b6'}}>{fmtKES(r.conf_revenue)}</td>
                      <td style={{padding:'7px 8px',color:'var(--textMuted)'}}>{r.guests_in}</td>
                      <td style={{padding:'7px 8px',color:'var(--textMuted)'}}>{r.guests_out}</td>
                      <td style={{padding:'7px 8px',color:'#2ecc71',fontWeight:700}}>{r.active_guests}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── EXPENSE MODAL ── */}

      {/* ── SHOPPING LIST APPROVAL ── */}
      {tab==='shopping_approval'&&(
        <ShoppingListApprovalPanel
          shoppingLists={shoppingLists}
          notify={notify}
          onUpdate={() => api.getShoppingLists().then(r=>setShoppingLists(r.data.results||r.data))}
        />
      )}

      {/* ── FUNDS DISPATCHED ── */}
      {tab==='funds_dispatched'&&(
        <FundsDispatchedPanel
          fundsDispatched={fundsDispatched}
          shoppingLists={shoppingLists}
          notify={notify}
          onUpdate={() => {
            api.getFundsDispatched().then(r=>setFundsDispatched(r.data.results||r.data));
            api.getShoppingLists().then(r=>setShoppingLists(r.data.results||r.data));
          }}
        />
      )}

      {/* ── AUDIT & LOG EXPORT ── */}
      {tab==='audit_export'&&(
        <>
          <AuditExportPanel notify={notify} />
          <div style={{ padding:"10px 14px", background:"#1a0a0a", border:"1px solid #e74c3c33", borderRadius:4, marginTop:8, fontSize:11, color:"#888" }}>ℹ️ Full Audit Log export has been moved to Admin Panel only (Admin tab → Reports).</div>
        </>
      )}

      {tab==='payroll'&&(
        <div style={{marginTop:-16}}><PayrollPage notify={notify} userRole="accountant"/></div>
      )}

      {tab==='donations'&&(
        <DonationPanel notify={notify} />
      )}

      {tab==='assets'&&<AssetsRegisterTab notify={notify} />}
      {tab==='hotel_reports'&&<HotelReportsTab notify={notify} period={period} dateFrom={dateFrom} dateTo={dateTo} setPeriod={setPeriod} setDateFrom={setDateFrom} setDateTo={setDateTo} />}
      {tab==='spoilage_review'&&<SpoilageReviewTab notify={notify} />}

      {expModal&&(
        <div style={{position:'fixed',inset:0,background:'var(--bgModal)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'var(--bgCard)',border:'1px solid #2a2a2a',borderRadius:6,padding:24,maxWidth:560,width:'100%', overflowX:'hidden',maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{fontSize:15,fontWeight:700,color:'var(--accent)',marginBottom:16}}>{editExpense?'Edit Expense':'Record New Expense'}</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:10}}>
              <Field label="Category *">
                <select style={inputStyle} value={expForm.category} onChange={e=>setExpForm(f=>({...f,category:e.target.value}))}>
                  {EXP_CATS.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Date *"><input type="date" style={inputStyle} value={expForm.expense_date} onChange={e=>setExpForm(f=>({...f,expense_date:e.target.value}))}/></Field>
              <div style={{gridColumn:'1/-1'}}>
                <Field label="Description *"><input style={inputStyle} placeholder="What was this expense for?" value={expForm.description} onChange={e=>setExpForm(f=>({...f,description:e.target.value}))}/></Field>
              </div>
              <Field label="Amount (KES) *"><input type="number" min="0" step="0.01" style={inputStyle} placeholder="0.00" value={expForm.amount} onChange={e=>setExpForm(f=>({...f,amount:e.target.value}))}/></Field>
              <Field label="Payment Mode">
                <select style={inputStyle} value={expForm.payment_mode} onChange={e=>setExpForm(f=>({...f,payment_mode:e.target.value}))}>
                  {PAY_MODES.map(m=><option key={m} value={m}>{m.toUpperCase()}</option>)}
                </select>
              </Field>
              <Field label="Vendor / Supplier"><input style={inputStyle} placeholder="Who was paid?" value={expForm.vendor} onChange={e=>setExpForm(f=>({...f,vendor:e.target.value}))}/></Field>
              <Field label="Invoice Number"><input style={inputStyle} placeholder="INV-001" value={expForm.invoice_number} onChange={e=>setExpForm(f=>({...f,invoice_number:e.target.value}))}/></Field>
              <div style={{gridColumn:'1/-1'}}>
                <Field label="Receipt Ref / Transaction Code"><input style={inputStyle} placeholder="M-Pesa code, bank ref…" value={expForm.receipt_ref} onChange={e=>setExpForm(f=>({...f,receipt_ref:e.target.value}))}/></Field>
              </div>
              <div style={{gridColumn:'1/-1'}}>
                <Field label="Notes"><textarea style={{...inputStyle,minHeight:70,resize:'vertical'}} placeholder="Additional notes…" value={expForm.notes} onChange={e=>setExpForm(f=>({...f,notes:e.target.value}))}/></Field>
              </div>
            </div>
            <div style={{display:'flex',gap:8,marginTop:14}}>
              <button style={{...btnPrimary,marginTop:0,flex:1}} onClick={saveExpense} disabled={savingExp}>{savingExp?'Saving…':editExpense?'Update Expense':'Record Expense'}</button>
              <button style={{...btnSm,padding:'11px 20px'}} onClick={()=>setExpModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Shopping List Approval Panel ─────────────────────────────────────────────
function ShoppingListApprovalPanel({ shoppingLists, notify, onUpdate }) {
  const [selected, setSelected] = useState(null);
  const [acctNotes, setAcctNotes] = useState('');
  const [rejReason, setRejReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [filterStatus, setFilterStatus] = useState('manager_approved');

  const filtered = shoppingLists.filter(sl =>
    filterStatus === 'all' || sl.status === filterStatus
  );

  const STATUS_COLORS = {
    pending:'#c8a96e', manager_approved:'#e67e22',
    accountant_approved:'#2ecc71', approved:'#2ecc71',
    rejected:'#e74c3c', purchased:'#3498db',
  };
  const STATUS_LABELS = {
    pending:'Pending Manager', manager_approved:'Awaiting Accountant',
    accountant_approved:'Fully Approved', approved:'Approved',
    rejected:'Rejected', purchased:'Purchased',
  };

  const acctApprove = async (sl) => {
    setProcessing(true);
    try {
      await api.accountantApproveShoppingList(sl.id, { notes: acctNotes });
      notify(`Shopping list ${sl.ref} approved! ✅`);
      setSelected(null); setAcctNotes('');
      onUpdate();
    } catch(e) { notify(e.response?.data?.error || 'Approval failed', 'error'); }
    finally { setProcessing(false); }
  };

  const reject = async (sl) => {
    if (!rejReason.trim()) { notify('Rejection reason required','error'); return; }
    setProcessing(true);
    try {
      await api.rejectShoppingList(sl.id, { reason: rejReason });
      notify(`Shopping list ${sl.ref} rejected`);
      setSelected(null); setRejReason('');
      onUpdate();
    } catch(e) { notify('Rejection failed','error'); }
    finally { setProcessing(false); }
  };

  if (selected) {
    const sc = STATUS_COLORS[selected.status] || '#888';
    const totalEst = (selected.items||[]).reduce((s,i)=>s+(parseFloat(i.qty_required)||0)*(parseFloat(i.estimated_cost)||0),0);
    return (
      <Card title={`Review: ${selected.ref} — ${selected.title}`} action={
        <button style={{ ...btnSm, background:'#2a2a1a', color:'var(--textMuted)' }} onClick={() => { setSelected(null); setAcctNotes(''); setRejReason(''); }}>← Back</button>
      }>
        {/* Approval progress */}
        <div style={{ display:'flex',gap:0,marginBottom:16,borderRadius:8,overflow:'hidden',border:'1px solid var(--border)' }}>
          {[
            { step:'1', label:'Submitted by Storekeeper', done:true, color:'#3498db' },
            { step:'2', label:'Manager Approved', done:['manager_approved','accountant_approved','purchased'].includes(selected.status), color:'#e67e22' },
            { step:'3', label:'Accountant Review', done:['accountant_approved','purchased'].includes(selected.status), color:'#2ecc71', active: selected.status==='manager_approved' },
            { step:'4', label:'Purchased', done:selected.status==='purchased', color:'#9b59b6' },
          ].map((s,i) => (
            <div key={i} style={{ flex:1,padding:'10px 12px',
                background:s.done?s.color+'33':s.active?s.color+'1a':'var(--bg3)',
                borderRight:'1px solid var(--border)',display:'flex',alignItems:'center',gap:6,
                outline:s.active?`2px solid ${s.color}44`:'none' }}>
              <span style={{ width:22,height:22,borderRadius:'50%',
                background:s.done?s.color:s.active?s.color+'88':'var(--border)',
                color:'white',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700 }}>{s.step}</span>
              <span style={{ fontSize:11,fontWeight:600,color:s.done||s.active?s.color:'var(--textMuted)' }}>{s.label}</span>
              {s.active && <span style={{ marginLeft:'auto',fontSize:10,color:s.color,fontWeight:700 }}>← You are here</span>}
            </div>
          ))}
        </div>

        <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:12,marginBottom:16,background:'var(--bgInput)',padding:12,borderRadius:6 }}>
          <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Created By</div><div style={{ fontWeight:600,color:'var(--textPrimary)' }}>{selected.created_by_name}</div></div>
          <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Manager Approved By</div><div style={{ fontWeight:600,color:'#e67e22' }}>{selected.approved_by_name}</div></div>
          <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Manager Approved At</div><div style={{ color:'var(--textSecond)' }}>{selected.approved_at ? new Date(selected.approved_at).toLocaleDateString('en-KE') : '—'}</div></div>
          <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Total Items</div><div style={{ fontWeight:700,color:'var(--textPrimary)' }}>{(selected.items||[]).length}</div></div>
          <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Estimated Total</div><div style={{ fontWeight:700,color:'var(--accent)',fontSize:16 }}>{fmtKES(totalEst)}</div></div>
          {selected.notes && <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Notes</div><div style={{ color:'var(--textSecond)' }}>{selected.notes}</div></div>}
        </div>

        {/* Approval stamps – show manager stamp now; accountant stamp after approval */}
        {['manager_approved','accountant_approved','purchased'].includes(selected.status) && (
          <div style={{
            display:'flex', gap:14, alignItems:'flex-start', flexWrap:'wrap',
            padding:'12px 14px', marginBottom:16,
            background:'rgba(200,169,110,0.05)',
            border:'1px solid rgba(200,169,110,0.15)', borderRadius:6,
          }}>
            <div style={{ width:'100%', fontSize:10, color:'#666', letterSpacing:2, textTransform:'uppercase', marginBottom:2 }}>
              Official Approval Stamps
            </div>
            <ApprovalStamp role="manager"
              by={selected.approved_by_name && selected.approved_by_name!=='—' ? selected.approved_by_name : null}
              at={selected.approved_at} size={80}/>
            {(selected.status==='accountant_approved'||selected.status==='purchased') && (
              <ApprovalStamp role="accountant"
                by={selected.accountant_approved_by_name && selected.accountant_approved_by_name!=='—' ? selected.accountant_approved_by_name : null}
                at={selected.accountant_approved_at} size={80}/>
            )}
          </div>
        )}

        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:16 }}>
          <thead><tr style={{ background:'var(--bgInput)' }}>
            {['Item','Current Stock','Reorder Level','Qty Required','Unit','Est. Unit Cost','Line Total'].map(h=>
              <th key={h} style={{ padding:'8px 10px',textAlign:'left',color:'var(--textSecond)',fontSize:10,fontWeight:700,borderBottom:'2px solid var(--border)' }}>{h}</th>
            )}
          </tr></thead>
          <tbody>
            {(selected.items||[]).map(item => (
              <tr key={item.id} style={{ borderBottom:'1px solid var(--border)' }}>
                <td style={{ padding:'8px 10px',fontWeight:600,color:'var(--textPrimary)' }}>{item.item_name}</td>
                <td style={{ padding:'8px 10px',color:'#e74c3c',fontWeight:600 }}>{parseFloat(item.current_stock||0)} {item.unit}</td>
                <td style={{ padding:'8px 10px',color:'var(--accent)' }}>{parseFloat(item.reorder_level||0)} {item.unit}</td>
                <td style={{ padding:'8px 10px',color:'#2ecc71',fontWeight:700 }}>{parseFloat(item.qty_required)}</td>
                <td style={{ padding:'8px 10px',color:'var(--textMuted)' }}>{item.unit}</td>
                <td style={{ padding:'8px 10px',color:'var(--textSecond)' }}>{fmtKES(item.estimated_cost)}</td>
                <td style={{ padding:'8px 10px',color:'var(--accent)',fontWeight:700 }}>{fmtKES(item.line_total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr style={{ background:'var(--bgInput)' }}>
            <td colSpan={6} style={{ padding:'8px 10px',fontWeight:700,textAlign:'right',color:'var(--textPrimary)' }}>TOTAL ESTIMATED COST</td>
            <td style={{ padding:'8px 10px',fontWeight:700,color:'var(--accent)',fontSize:15 }}>{fmtKES(totalEst)}</td>
          </tr></tfoot>
        </table>

        {selected.status === 'manager_approved' && (
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:16,background:'var(--bgInput)',padding:16,borderRadius:8,border:'1px solid var(--border)' }}>
            <div>
              <div style={{ fontWeight:700,color:'#2ecc71',marginBottom:8 }}>✅ Approve Funds</div>
              <Field label="Accountant Notes (optional)">
                <textarea style={{ ...inputStyle, minHeight:70 }} value={acctNotes}
                  placeholder="Budget notes, conditions, remarks..." onChange={e=>setAcctNotes(e.target.value)} />
              </Field>
              <button style={{ ...btnPrimary, background:'linear-gradient(135deg,#27ae60,#1e8449)', marginTop:8, width:'100%' }}
                onClick={() => acctApprove(selected)} disabled={processing}>
                {processing ? 'Processing…' : `✅ Approve — Authorise KES ${fmtKES(totalEst)}`}
              </button>
            </div>
            <div>
              <div style={{ fontWeight:700,color:'#e74c3c',marginBottom:8 }}>✕ Reject</div>
              <Field label="Rejection Reason *">
                <textarea style={{ ...inputStyle, minHeight:70 }} value={rejReason}
                  placeholder="Reason for rejection..." onChange={e=>setRejReason(e.target.value)} />
              </Field>
              <button style={{ ...btnPrimary, background:'linear-gradient(135deg,#c0392b,#96281b)', marginTop:8, width:'100%' }}
                onClick={() => reject(selected)} disabled={processing || !rejReason.trim()}>
                ✕ Reject Shopping List
              </button>
            </div>
          </div>
        )}
        {selected.status === 'accountant_approved' && (
          <div style={{ padding:'12px 16px',background:'var(--bgSuccess)',borderRadius:8,color:'#2ecc71',fontWeight:600 }}>
            ✅ You have already approved this shopping list.
            {selected.accountant_notes && <div style={{ fontWeight:400,marginTop:4,color:'var(--textMuted)' }}>Notes: {selected.accountant_notes}</div>}
          </div>
        )}
      </Card>
    );
  }

  return (
    <div>
      <div style={{ display:'flex',gap:8,marginBottom:14,flexWrap:'wrap',alignItems:'center' }}>
        {['all','pending','manager_approved','accountant_approved','rejected','purchased'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{
            ...btnSm, fontSize:11,
            background: filterStatus===s ? (STATUS_COLORS[s]||'#c8a96e')+'33' : 'var(--bgInput)',
            color: filterStatus===s ? (STATUS_COLORS[s]||'#c8a96e') : 'var(--textMuted)',
            border:`1px solid ${filterStatus===s?(STATUS_COLORS[s]||'#c8a96e')+'66':'var(--border)'}`,
          }}>{s==='all'?'All':STATUS_LABELS[s]||s} {s!=='all'?`(${shoppingLists.filter(sl=>sl.status===s).length})`:''}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign:'center',padding:40,color:'var(--textMuted)',fontSize:13 }}>
          No shopping lists with status: {filterStatus}
        </div>
      ) : (
        <div style={{ display:'grid',gap:10 }}>
          {filtered.map(sl => {
            const sc = STATUS_COLORS[sl.status] || '#888';
            const totalEst = (sl.items||[]).reduce((s,i)=>s+(parseFloat(i.qty_required)||0)*(parseFloat(i.estimated_cost)||0),0);
            return (
              <div key={sl.id} style={{ background:'var(--bgInput)',border:`1px solid ${sc}33`,
                                         borderLeft:`4px solid ${sc}`,borderRadius:6,padding:'14px 16px',
                                         display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex',gap:8,alignItems:'center',marginBottom:4 }}>
                    <span style={{ fontWeight:700,color:'var(--accent)',fontFamily:'monospace',fontSize:14 }}>{sl.ref}</span>
                    <span style={{ padding:'2px 8px',borderRadius:3,fontSize:10,fontWeight:700,
                                   background:`${sc}22`,color:sc }}>{STATUS_LABELS[sl.status]||sl.status}</span>
                  </div>
                  <div style={{ fontWeight:600,color:'var(--textPrimary)',marginBottom:4 }}>{sl.title}</div>
                  <div style={{ fontSize:12,color:'var(--textSecond)',display:'flex',gap:16,flexWrap:'wrap' }}>
                    <span>📋 {(sl.items||[]).length} items</span>
                    <span style={{ color:'var(--accent)',fontWeight:700 }}>Est: {fmtKES(totalEst)}</span>
                    <span>By: {sl.created_by_name}</span>
                    {sl.approved_by_name && sl.approved_by_name!=='—' && <span style={{ color:'#e67e22' }}>Mgr: {sl.approved_by_name}</span>}
                    <span style={{ color:'var(--textMuted)' }}>{new Date(sl.created_at).toLocaleDateString('en-KE')}</span>
                  </div>
                  {sl.rejection_reason && <div style={{ marginTop:4,fontSize:11,color:'#e74c3c' }}>Rejected: {sl.rejection_reason}</div>}
                </div>
                <div style={{ display:'flex',gap:8 }}>
                  <button style={{ ...btnSm, background:'#1a2a3a', color:'#3498db' }} onClick={() => setSelected(sl)}>
                    {sl.status==='manager_approved' ? '🔍 Review & Approve' : '👁 View'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ─── Funds Dispatched Panel ────────────────────────────────────────────────────
function FundsDispatchedPanel({ fundsDispatched, shoppingLists, notify, onUpdate }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ shopping_list:'',amount:'',method:'cash',dispatched_to:'',reference:'',notes:'' });
  const [saving, setSaving] = useState(false);

  const totalDispatched = fundsDispatched.reduce((s,f) => s + parseFloat(f.amount||0), 0);
  const approvedLists = shoppingLists.filter(s => ['manager_approved','accountant_approved'].includes(s.status));

  const submit = async () => {
    if (!form.amount || !form.dispatched_to) { notify('Amount and recipient are required','error'); return; }
    setSaving(true);
    try {
      // Record the fund dispatch
      await api.createFundsDispatched(form);
      // Automatically record as an expense entry
      try {
        await api.createExpense({
          category: 'other',
          description: `Funds Dispatched to ${form.dispatched_to}${form.notes ? ' — ' + form.notes : ''}`,
          amount: form.amount,
          vendor: form.dispatched_to,
          expense_date: new Date().toISOString().slice(0,10),
          payment_mode: form.method,
          receipt_ref: form.reference || '',
          notes: `Auto-recorded from Funds Dispatched. Shopping list ref: ${form.shopping_list || 'N/A'}`,
        });
        notify('Funds dispatched & recorded as expense ✅');
      } catch {
        // Don't fail the whole operation if expense auto-record fails
        notify('Funds dispatched recorded ✅ (expense auto-record may need manual entry)', 'success');
      }
      setShowForm(false);
      setForm({ shopping_list:'',amount:'',method:'cash',dispatched_to:'',reference:'',notes:'' });
      onUpdate();
    } catch(e) { notify(e.response?.data?.detail || 'Error saving','error'); }
    finally { setSaving(false); }
  };

  const doDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try { await api.deleteFundsDispatched(id); notify('Deleted'); onUpdate(); }
    catch { notify('Delete failed','error'); }
  };

  const METHOD_COLORS = { cash:'#2ecc71', mpesa:'#27ae60', bank:'#3498db', cheque:'#9b59b6' };

  return (
    <div>
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14 }}>
        <div style={{ background:'var(--bgInput)',border:'1px solid #c8a96e44',borderRadius:6,padding:'10px 16px',display:'flex',gap:20 }}>
          <div>
            <div style={{ fontSize:10,color:'var(--textMuted)' }}>Total Dispatched</div>
            <div style={{ fontWeight:700,color:'var(--accent)',fontSize:20 }}>{fmtKES(totalDispatched)}</div>
          </div>
          <div>
            <div style={{ fontSize:10,color:'var(--textMuted)' }}>Transactions</div>
            <div style={{ fontWeight:700,color:'var(--textPrimary)',fontSize:20 }}>{fundsDispatched.length}</div>
          </div>
        </div>
        <button style={btnPrimary} onClick={() => setShowForm(p => !p)}>
          {showForm ? '✕ Cancel' : '+ Record Funds Dispatched'}
        </button>
      </div>

      {showForm && (
        <Card title="Record Funds Dispatched" style={{ marginBottom:16 }}>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:12 }}>
            <Field label="Link to Shopping List (optional)">
              <select style={inputStyle} value={form.shopping_list} onChange={e=>setForm(p=>({...p,shopping_list:e.target.value}))}>
                <option value="">— None —</option>
                {approvedLists.map(sl => <option key={sl.id} value={sl.id}>{sl.ref} — {sl.title}</option>)}
              </select>
            </Field>
            <Field label="Amount (KES) *">
              <input type="number" style={inputStyle} placeholder="0.00" value={form.amount}
                onChange={e=>setForm(p=>({...p,amount:e.target.value}))} />
            </Field>
            <Field label="Payment Method">
              <select style={inputStyle} value={form.method} onChange={e=>setForm(p=>({...p,method:e.target.value}))}>
                <option value="cash">Cash</option>
                <option value="mpesa">M-Pesa</option>
                <option value="bank">Bank Transfer</option>
                <option value="cheque">Cheque</option>
              </select>
            </Field>
            <Field label="Dispatched To *">
              <input style={inputStyle} placeholder="Person or vendor name" value={form.dispatched_to}
                onChange={e=>setForm(p=>({...p,dispatched_to:e.target.value}))} />
            </Field>
            <Field label="Reference (M-Pesa code, cheque no., etc.)">
              <input style={inputStyle} placeholder="Reference number" value={form.reference}
                onChange={e=>setForm(p=>({...p,reference:e.target.value}))} />
            </Field>
            <Field label="Notes">
              <input style={inputStyle} placeholder="Optional notes" value={form.notes}
                onChange={e=>setForm(p=>({...p,notes:e.target.value}))} />
            </Field>
          </div>
          <div style={{ marginTop:14, display:'flex',gap:8 }}>
            <button style={btnPrimary} onClick={submit} disabled={saving}>{saving?'Saving…':'💵 Record Dispatch'}</button>
            <button style={{ ...btnSm,background:'var(--bgInput)',color:'var(--textMuted)' }} onClick={()=>setShowForm(false)}>Cancel</button>
          </div>
        </Card>
      )}

      {fundsDispatched.length === 0 ? (
        <div style={{ textAlign:'center',padding:40,color:'var(--textMuted)',fontSize:13 }}>No funds dispatched yet</div>
      ) : (
        <Card title={`Funds Dispatched Log (${fundsDispatched.length} records)`}>
          <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
            <thead><tr style={{ background:'var(--bgInput)' }}>
              {['Ref','Date','Amount','Method','To','Reference','Shopping List','Notes','By',''].map(h =>
                <th key={h} style={{ padding:'8px 10px',textAlign:'left',color:'var(--textSecond)',fontSize:10,fontWeight:700,borderBottom:'2px solid var(--border)' }}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {fundsDispatched.map(f => (
                <tr key={f.id} style={{ borderBottom:'1px solid var(--border)' }}>
                  <td style={{ padding:'8px 10px',fontFamily:'monospace',color:'var(--accent)',fontWeight:700 }}>{f.ref}</td>
                  <td style={{ padding:'8px 10px',fontSize:11,color:'var(--textMuted)' }}>{new Date(f.dispatched_at).toLocaleDateString('en-KE')}</td>
                  <td style={{ padding:'8px 10px',fontWeight:700,color:'var(--accent)' }}>{fmtKES(f.amount)}</td>
                  <td style={{ padding:'8px 10px' }}>
                    <span style={{ padding:'2px 7px',borderRadius:3,fontSize:10,fontWeight:700,
                      background:(METHOD_COLORS[f.method]||'#888')+'22',color:METHOD_COLORS[f.method]||'#888',textTransform:'uppercase' }}>
                      {f.method}
                    </span>
                  </td>
                  <td style={{ padding:'8px 10px',fontWeight:600,color:'var(--textPrimary)' }}>{f.dispatched_to}</td>
                  <td style={{ padding:'8px 10px',color:'var(--textSecond)',fontFamily:'monospace',fontSize:11 }}>{f.reference||'—'}</td>
                  <td style={{ padding:'8px 10px',color:'var(--textMuted)',fontSize:11 }}>{f.shopping_list_ref||'—'}</td>
                  <td style={{ padding:'8px 10px',color:'var(--textMuted)',fontSize:11 }}>{f.notes||'—'}</td>
                  <td style={{ padding:'8px 10px',color:'var(--textMuted)',fontSize:11 }}>{f.dispatched_by_name}</td>
                  <td style={{ padding:'8px 10px' }}>
                    <button className="btn btn-danger btn-sm" onClick={() => doDelete(f.id)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr style={{ background:'var(--bgInput)' }}>
              <td colSpan={2} style={{ padding:'8px 10px',fontWeight:700,textAlign:'right',color:'var(--textPrimary)' }}>TOTAL</td>
              <td style={{ padding:'8px 10px',fontWeight:700,color:'var(--accent)',fontSize:14 }}>{fmtKES(totalDispatched)}</td>
              <td colSpan={7} />
            </tr></tfoot>
          </table>
        </Card>
      )}
    </div>
  );
}


// ─── Audit & Log Export Panel ──────────────────────────────────────────────────
function AuditExportPanel({ notify, isAdmin }) {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [category, setCategory] = useState('');
  const [exporting, setExporting] = useState('');

  const doExport = async (type) => {
    setExporting(type);
    let url, filename;
    let params = '?';
    if (dateFrom) params += `date_from=${dateFrom}&`;
    if (dateTo)   params += `date_to=${dateTo}&`;
    if (type === 'audit') {
      url = api.exportAuditLogExcel(params);
      filename = `${getBrandingSlug()}_AuditLog_${new Date().toISOString().slice(0,10)}.xlsx`;
    } else if (type === 'movement_pdf') {
      if (category) params += `category=${category}&`;
      url = api.exportMovementLogPdf(params);
      filename = `${getBrandingSlug()}_StockMovement_${new Date().toISOString().slice(0,10)}.pdf`;
    } else {
      if (category) params += `category=${category}&`;
      url = api.exportMovementLogExcel(params);
      filename = `${getBrandingSlug()}_MovementLog_${new Date().toISOString().slice(0,10)}.xlsx`;
    }
    try {
      const res = await axiosClient.get(url, { responseType: 'blob', baseURL: '' });
      // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res.data;
      if (blob.size < 50) throw new Error('Empty file returned');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 1000);
      notify(`${type === 'audit' ? 'Audit log' : 'Movement log'} exported ✓`);
    } catch(e) {
      notify(`Export failed: ${await readBlobError(e)}`, 'error');
    } finally {
      setExporting('');
    }
  };

  const CATEGORIES = [
    'food','beverage','cleaning','linen','stationery','maintenance','amenities','chemicals','uniforms','other'
  ];

  return (
    <div style={{ display:'grid',gridTemplateColumns:'1fr',gap:20, maxWidth:520 }}>
      <div style={{ padding:'10px 14px', background:'var(--bgInfo)', border:'1px solid #3498db44', borderRadius:6, marginBottom:4, fontSize:12, color:'var(--textMuted)' }}>
        ℹ️ Accounting access is limited to <strong>Stock Movement</strong> exports only. Full Audit Log export is available in the Admin panel.
      </div>

      <Card title="📦 Stock Movement Log Export">
        <p style={{ color:'var(--textSecond)',fontSize:13,marginBottom:16 }}>
          Export all stock movements — purchases received, issuances, write-offs, and adjustments.
        </p>
        <div style={{ display:'grid',gap:10,marginBottom:16 }}>
          <Field label="Date From">
            <input type="date" style={inputStyle} value={dateFrom} onChange={e=>setDateFrom(e.target.value)} />
          </Field>
          <Field label="Date To">
            <input type="date" style={inputStyle} value={dateTo} onChange={e=>setDateTo(e.target.value)} />
          </Field>
          <Field label="Category (optional)">
            <select style={inputStyle} value={category} onChange={e=>setCategory(e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:8 }}>
          <button style={{ ...btnPrimary, background:'linear-gradient(135deg,#27ae60,#1e8449)', padding:'10px' }}
            onClick={() => doExport('movement')} disabled={exporting==='movement'}>
            {exporting==='movement' ? '⏳…' : '📊 Excel'}
          </button>
          <button style={{ ...btnPrimary, background:'linear-gradient(135deg,#c0392b,#922b21)', padding:'10px' }}
            onClick={() => doExport('movement_pdf')} disabled={exporting==='movement_pdf'}>
            {exporting==='movement_pdf' ? '⏳…' : '📄 PDF'}
          </button>
        </div>
        <p style={{ fontSize:11,color:'var(--textMuted)',marginTop:8 }}>
          Exports up to 5,000 most recent stock movements.
        </p>
      </Card>
    </div>
  );
}